import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import OrderTable from "../../components/Table/OrderTable.jsx";
import Card from "../../components/Table/Card.jsx";
import CustomButton from "../../components/Form/ArrowButton.jsx";
import Table from "../../components/Table/Table.jsx";
import { useThunk } from "../../hooks/useThunk";
import { createPaypalCheckoutForTopup, fetchPaymentHistory, getUserWallet } from "../../store";
import showToast from "../../utils/toastNotifications";
import { currencyFormatter } from "../../utils/formatting.js";
import { getTokenPayload } from "../../utils/getTokenPayload";
import { useSelector } from "react-redux";
import { formatOrderCreatedDate, formatPaymentDate, formatYYYY_MM_DD } from "../../utils/dateFormating.js";
import Pagination from "../../components/Table/pagination.jsx";

const WriterWallets = () => {

  const userData = useSelector((state) => state.auth.user);
  const wallet = useSelector((state) => state.user.wallet)// wallet 
  const isGetUserWallet = useSelector((state) => state.user.isWalletFetching) //wallet fetching state
  const tokenPayload = getTokenPayload();
  const [value, setValue] = useState("");
  const [paymentHistoryFetched, setPaymentHistoryFetched] = useState([])

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;
  const [pagination, setPagination] = useState()
  
  // defining custom hooks ===============================================================

  const [doGetUserWallet] = useThunk(getUserWallet);
  const [doCreateTopupCheckout, isCreateTopupCheckout, errorCreateTopupCheckout] = useThunk(createPaypalCheckoutForTopup);
  const [doFetchPaymentHistory, isHistoryFetching, errorFetchingHistory] = useThunk(fetchPaymentHistory);

  // defining side effect ================================================================

  // fetching current user wallet
  useEffect(() => {
    if(!(isGetUserWallet === null) && !(isGetUserWallet === true)){ // avoid multiple fetching requests on page refresh
      fetchUserWalletMethod();
    }
  }, [])

  useEffect(() => {
    fetchUserPaymentHistory()
  },[currentPage])


  // defining functions ==================================================================
  
  // sync user input values
  const handleChange = (e) => {
    const inputValue = e.target.value;
    // Allow only numeric values and a single dot for decimals
    if (/^\d*\.?\d*$/.test(inputValue)) {
      setValue(inputValue);
    }
  };

  // initiate topup functionality
  const handleTopupWalletWithPayPal = async () => {

    if (!wallet) {
      showToast("error", "Wallet not found");
      return;
    }
  
    if(!value || value < 1){
      showToast("error", "Invalid amount");
      return;
    }
    // setLoading(true);
  
    // Prepare topup data to send to the server
    const topUp = {
      items: [
        {
          id: `${tokenPayload?._id} TOPUP ${Date.now()}`,
          name: `${tokenPayload?._id} TOPUP $ ${value} ${Date.now()}`,
          description: `${userData.firstName} ${userData.lastName} (${tokenPayload?._id}) top up the wallet with $ ${value} at ${new Date()}`,
          quantity: 1,
          unit_price: parseFloat(value) || 0,
        },
      ],
      totalAmount: parseFloat(value) || 0,
      currency: "USD",
    };
  
    try {
      const result = await doCreateTopupCheckout(topUp);
      
      if (result?.success) {
        const { approveUrl } = result?.response;
        window.location.href = approveUrl;
      } else {
        showToast("error", result?.error?.message || "Failed to create PayPal checkout session");
      }
    } catch (error) {
      showToast("error", error.message || "Error during payment initiation");
    }
  };
  
  // fetching current user wallet
  const fetchUserWalletMethod = async() => {
    const result = await doGetUserWallet();
    if(!result?.success){  
      showToast("error", result?.error?.message || "Error Occurred")
    }    
  };

  // fetch payment history 
  const fetchUserPaymentHistory = async () => {
    const page = currentPage;
    const offset = (page-1) * itemsPerPage;
    const limit = itemsPerPage;
    const result = await doFetchPaymentHistory({offset, limit});
    
    if(result.success){

      const data = result.response?.data?.data
      const updatedData = data?.map((data) =>{
        return {
          ...data, 
          createdAt: formatYYYY_MM_DD(data.createdAt || ''),
          order: data.order || "N/A",
          amount: currencyFormatter.format(data.amount || 0.00) || "$ 00.00",
          paymentType: data.paymentType === "MANUAL" ? "Manual" : data.paymentType,
        }
      })
      setPaymentHistoryFetched(updatedData)
      setPagination(result.response?.data?.pagination)

    }else{
      showToast("error", result.error?.message)
      setPaymentHistoryFetched([])
    }
  }

  //handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  return (
    <div
      className={`min-[80%] mt-24 px-4 xl:px-8  flex flex-col w-full xl:w-[calc(100vw-256px)]`}
    >
      {" "}
      <h1 className="text-primary xl:hidden font-roboto font-semibold text-[24px] leading-5 py-2">
        My Wallet
      </h1>
      <div className="flex flex-col w-auto">
        <div className="bg-[#E4F1FF] lg:ml-2 flex flex-col space-y-4 sm:space-y-0 sm:flex-row items-start sm:items-center justify-between mt-4 xl:mt-0 w-full h-auto rounded-lg px-4  md:px-6 py-3 mb-6 xl:mb-[24px] ">
          <div className="flex items-end space-x-4 justify-between w-full sm:w-auto">
            <div className="flex flex-col space-y-2 font-roboto">
              <p className="text-base md:text-lg xl:text-level-2 text-secondary leading-[24px] md:leading-[30px]">
                Amount{" "}
              </p>
              <div className="flex items-center  border border-b-secondary border-t-transparent border-x-transparent bg-transparent">
                <span className="font-roboto text-[40px] leading-[40px]">
                  $
                </span>
                <input
                  type="text"
                  value={value}
                  onChange={handleChange}
                  className="bg-transparent ml-2 font-roboto text-[40px] leading-[40px] outline-none w-auto max-w-[100px]"
                  placeholder="00.00"
                />
              </div>
            </div>

            <CustomButton
              text={
                isCreateTopupCheckout ?
                  <ClipLoader
                    loading={isCreateTopupCheckout}
                    speedMultiplier={0.5}
                    size={22}
                    color="#60a5fa"
                  /> 
                  :"Top Up"
              }
              onClick={() => {handleTopupWalletWithPayPal()}}
              width="120px"
              height="44px"
              iconDirection=""
              iconPosition=""
              disabled={isCreateTopupCheckout}
            />

          </div>
          <div className="flex flex-col w-full h-[100px] sm:w-auto space-y-2 font-roboto bg-blue-shades px-6 py-[12px] rounded-[8px]">
            <div className="flex h-[40px]">
              <p className="text-base md:text-lg xl:text-level-2 text-white leading-[24px] md:leading-[30px]">
                Wallet Balance
              </p>
            </div>
            <div className="flex justify-center items-center h-[50px]">
              {isGetUserWallet && (
                  <>
                    <ClipLoader
                      loading={isGetUserWallet}
                      speedMultiplier={0.5}
                      size={22}
                      color="#60a5fa"
                    />
                  </>
                )
              }
              {!isGetUserWallet && (
                <p className="text-lg md:text-xl xl:text-level-1 text-white leading-[32px] md:leading-[40px]">
                  {currencyFormatter.format(wallet?.amount || 0.00) || "$ 00.00"}
                </p>
                )
              }
            </div>
          </div>
        </div>
      </div>
      {/* <h1 className="text-primary  font-roboto font-semibold text-[20px] leading-5 py-2">
        Payment History
      </h1> */}
    <div className="flex flex-col relative flex-grow font-inter ">
      <div className=" lg:ml-2 pb-16">
        <Table
          columns={columns}
          data={paymentHistoryFetched}
          className=""
          minWidth="min-w-[1000px]"
          headerClassName="bg-gray-200"
          loading={isHistoryFetching}
          error={errorFetchingHistory}
        />
      </div>
      
      {/*<div className="lg:hidden block ">
        <Card
          data={paginatedData}
          loading={isHistoryFetching} // Ensure this matches the loading state
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          pagination={{ totalDocuments: data.length }}
          onPageChange={(page) => setCurrentPage(page)}
          className="my-custom-class"
        />
      </div>*/}

       {/* Pagination Controls */} 
        <div className=" lg:mb-5">
          <Pagination
            totalItems={pagination?.totalDocuments || 0}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            className="absolute bottom-0 left-1/2 transform -translate-x-1/2 mb-4"
          />
        </div>
      </div>
    </div>
  );
};

export default WriterWallets;

const columns = [
  { label: "Payment ID", key: "paymentId" },
  { label: "Order ID", key: "order" },
  { label: "Date", key: "createdAt" },
  { label: "Amount", key: "amount" },
  { label: "Payment Type", key: "paymentType" },
  { label: "Transaction Type", key: "transactionType" },
  { label: "Reason", key: "reason" },
];
